/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useUserUnit } from "../../hooks";
import { MlFeature, MlTarget, NormalizeFeaturePackage } from "./types";
import { getNormalizedByLabel } from "./utils/nameUtils";

interface InvalidDataDetailTableProps {
  getFeatureDisplayName?: (feature: MlFeature) => string;
}

const InvalidDataDetailTable: FC<InvalidDataDetailTableProps> = ({
  getFeatureDisplayName
}) => {
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const unitType = useUserUnit();
  const mlState = useSelector((state: RootState) => state.ml);
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const featuresWithInvalidData = mlState.warnDetails?.featuresWithNaOrZero || [];
  const featuresThatCanBeZero = mlState.warnDetails?.featuresThatCanBeZero || [];
  const droppedWells = mlState.warnDetails?.droppedWells || [];
  const inputFeatures: MlFeature[] = (mlState.inputFeatures as MlFeature[]) || [];
  const targetFeature: MlTarget = mlState.targetFeature as MlTarget;
  const normalizationSummary = mlState.warnDetails?.normalizationSummary ?? [];

  const normalizationLookup = useMemo(() => {
    const map = new Map<string, NormalizeFeaturePackage>();
    normalizationSummary.forEach((item) => {
      if (item?.ApplyTo?.Property) {
        map.set(item.ApplyTo.Property, item);
      }
    });
    return map;
  }, [normalizationSummary]);

  const calculationFeatureKeys = useMemo(() => {
    if (droppedWells.length === 0) return new Set<string>();
    return new Set(Object.keys(droppedWells[0] as object));
  }, [droppedWells]);

  const calculationInputFeatures = useMemo(
    () => inputFeatures.filter((f) => calculationFeatureKeys.has(f.Property)),
    [inputFeatures, calculationFeatureKeys]
  );

  const activeInvalidFeatureCount = useMemo(
    () =>
      featuresWithInvalidData.filter((prop) => calculationFeatureKeys.has(prop)).length,
    [featuresWithInvalidData, calculationFeatureKeys]
  );

  const columns: ColumnsType<any> = useMemo(() => {
    if (!droppedWells || droppedWells.length === 0) return [];

    const allFeatures = [...calculationInputFeatures];
    allFeatures.unshift(
      {
        Property: "Uwi", //Property from backend returns key as 'Uwi' instead of 'Header.EntityName'
        NiceName: "Entity Name"
      } as MlFeature,
      {
        Property: targetFeature.Property, // add the target as feature
        NiceName: targetFeature.NiceName
      } as MlFeature
    );

    return allFeatures.map((feature) => {
      const displayName = getFeatureDisplayName
        ? getFeatureDisplayName(feature)
        : feature.Property;

      const normEntry = normalizationLookup.get(feature.Property);
      const normalizedByText = getNormalizedByLabel(
        normEntry?.NormalizeBy,
        unitType,
        categoryList
      );

      const columnTitle = normalizedByText ? (
        <ColumnTitle>
          <span>{displayName}</span>
          <NormalizedByLabel>{normalizedByText}</NormalizedByLabel>
        </ColumnTitle>
      ) : (
        displayName
      );

      return {
        title: columnTitle,
        dataIndex: feature.Property,
        key: feature.Property,
        ellipsis: true,
        width: 200,
        render: (value: any) => {
          // Handle different value types
          if (value === null || value === undefined || value === "-") {
            return <NAValue>n/a</NAValue>;
          }

          // Check if value is 0 and feature cannot be zero
          if (value === 0 && !featuresThatCanBeZero.includes(feature.Property)) {
            return <NAValue>{value}</NAValue>;
          }

          if (typeof value === "object") {
            return JSON.stringify(value);
          }
          return String(value);
        }
      };
    });
  }, [
    droppedWells,
    calculationInputFeatures,
    featuresThatCanBeZero,
    normalizationLookup,
    unitType,
    categoryList
  ]);

  const dataSource = useMemo(() => {
    return droppedWells.map((well, idx) => ({
      ...well,
      key: idx
    }));
  }, [droppedWells]);

  return (
    <Container>
      <HeaderSection>
        <TargetFeatureName>
          {targetFeature.NiceName || "Target Feature"}
        </TargetFeatureName>
        <InfoText>
          Total features with invalid data: {activeInvalidFeatureCount}
          <br />
          Total dropped wells: {droppedWells.length}
          <br />
          Some features allow zero values. Features that do not will have values
          highlighted in red.
        </InfoText>
      </HeaderSection>
      <TableContainer>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["25", "50", "100", "200"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }
          }}
          sticky
          size="small"
          bordered
          scroll={{ x: "max-content", y: "calc(70vh - 220px)" }}
        />
      </TableContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  font-size: 14px;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  margin-bottom: 8px;
`;

const TargetFeatureName = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const InfoText = styled.div`
  font-size: 14px;
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 0;
  .ant-table-pagination.ant-pagination {
    margin: 16px 0 0;
  }
  .ant-table-body {
    overflow: auto !important;
  }
`;

const NAValue = styled.span`
  color: var(--color-danger);
  font-weight: bold;
`;

const ColumnTitle = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
`;

const NormalizedByLabel = styled.span`
  font-size: 11px;
  color: var(--color-text-60);
  font-weight: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default InvalidDataDetailTable;
